import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { InventoryMovementTypeSchema } from "./commercial.js";
import { allocateMovingAverageOutboundFen } from "./purchase-return.js";

const SafeIntegerSchema = Type.Integer({
  minimum: Number.MIN_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const NonNegativeSafeIntegerSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const InventoryCountQuantitySchema = Type.Integer({ minimum: 0, maximum: 1_000_000 });
const InventoryCountIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$",
});
const InventoryCountNumberSchema = Type.String({
  pattern: "^PD-[0-9]{8}-[0-9A-F]{12}$",
});
const InventoryCountDateTimeSchema = Type.String({
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]{3})?Z$",
});

/**
 * Frozen facts captured when a manager submits a physical count for approval.
 * Normal physical movements may continue afterwards; the original discrepancy
 * is carried forward instead of overwriting those later movements.
 */
export const InventoryCountCaptureSchema = Type.Object(
  {
    captureVersion: Type.Literal(1),
    countNumber: InventoryCountNumberSchema,
    inventoryMovementSequence: NonNegativeSafeIntegerSchema,
    inventoryVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    theoreticalQuantity: SafeIntegerSchema,
    countedQuantity: InventoryCountQuantitySchema,
    differenceQuantity: SafeIntegerSchema,
    inventoryValueFen: Type.Union([NonNegativeSafeIntegerSchema, Type.Null()]),
    inventoryValuationVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    surplusUnitCostFen: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    capturedAt: InventoryCountDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryCountCapture = Static<typeof InventoryCountCaptureSchema>;

export const InventoryCountPeriodMovementSchema = Type.Object(
  {
    sequence: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    movementId: InventoryCountIdSchema,
    movementType: InventoryMovementTypeSchema,
    quantityDelta: SafeIntegerSchema,
    quantityBefore: SafeIntegerSchema,
    quantityAfter: SafeIntegerSchema,
    referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    occurredAt: InventoryCountDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryCountPeriodMovement = Static<typeof InventoryCountPeriodMovementSchema>;

export const InventoryCountProgressReadModelSchema = Type.Object(
  {
    kind: Type.Literal("INVENTORY_COUNT_PROGRESS"),
    approvalId: InventoryCountIdSchema,
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    capture: InventoryCountCaptureSchema,
    periodMovements: Type.Array(InventoryCountPeriodMovementSchema, { maxItems: 500 }),
    periodMovementCount: Type.Integer({ minimum: 0, maximum: 501 }),
    periodMovementDelta: SafeIntegerSchema,
    currentTheoreticalQuantity: SafeIntegerSchema,
    projectedQuantityAfterApply: SafeIntegerSchema,
    activeReservedQuantity: NonNegativeSafeIntegerSchema,
    currentInventoryVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    currentInventoryValuationVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    currentInventoryValueFen: Type.Union([NonNegativeSafeIntegerSchema, Type.Null()]),
    projectedInventoryValueFen: Type.Union([NonNegativeSafeIntegerSchema, Type.Null()]),
    inventoryValueDeltaFen: Type.Union([SafeIntegerSchema, Type.Null()]),
    canExecute: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: InventoryCountDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryCountProgressReadModel = Static<typeof InventoryCountProgressReadModelSchema>;

export const InventoryCountAppliedFactSchema = Type.Object(
  {
    factVersion: Type.Literal(1),
    approvalId: InventoryCountIdSchema,
    countNumber: InventoryCountNumberSchema,
    movementId: InventoryCountIdSchema,
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    capture: InventoryCountCaptureSchema,
    executionInventoryMovementSequence: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    periodMovementCount: Type.Integer({ minimum: 0, maximum: 500 }),
    periodMovementDelta: SafeIntegerSchema,
    quantityBefore: SafeIntegerSchema,
    quantityDelta: SafeIntegerSchema,
    quantityAfter: SafeIntegerSchema,
    inventoryVersionBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryVersionAfter: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryValueBeforeFen: NonNegativeSafeIntegerSchema,
    inventoryValueDeltaFen: SafeIntegerSchema,
    inventoryValueAfterFen: NonNegativeSafeIntegerSchema,
    inventoryValuationVersionBefore: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    inventoryValuationVersionAfter: Type.Integer({
      minimum: 2,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    actorId: InventoryCountIdSchema,
    authorizerId: InventoryCountIdSchema,
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    occurredAt: InventoryCountDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryCountAppliedFact = Static<typeof InventoryCountAppliedFactSchema>;

export const InventoryCountBusinessEventPayloadSchema = Type.Object(
  { inventoryCount: InventoryCountAppliedFactSchema },
  { additionalProperties: false },
);
export type InventoryCountBusinessEventPayload = Static<
  typeof InventoryCountBusinessEventPayloadSchema
>;

export function isInventoryCountCapture(value: unknown): value is InventoryCountCapture {
  if (!Value.Check(InventoryCountCaptureSchema, value)) return false;
  const difference = value.countedQuantity - value.theoreticalQuantity;
  if (!Number.isSafeInteger(difference) || difference !== value.differenceQuantity) return false;
  if ((value.inventoryValueFen === null) !== (value.inventoryValuationVersion === null))
    return false;
  return difference > 0 ? value.surplusUnitCostFen !== null : value.surplusUnitCostFen === null;
}

function expectedInventoryCountValueDeltaFen(
  capture: InventoryCountCapture,
  quantityBefore: number,
  inventoryValueBeforeFen: number,
): number | null {
  try {
    if (capture.differenceQuantity < 0) {
      return -allocateMovingAverageOutboundFen(
        inventoryValueBeforeFen,
        quantityBefore,
        Math.abs(capture.differenceQuantity),
      );
    }
    if (capture.differenceQuantity > 0 && capture.surplusUnitCostFen !== null) {
      const increase = capture.differenceQuantity * capture.surplusUnitCostFen;
      return Number.isSafeInteger(increase) ? increase : null;
    }
  } catch {
    return null;
  }
  return null;
}

export function isInventoryCountProgressReadModel(
  value: unknown,
): value is InventoryCountProgressReadModel {
  if (!Value.Check(InventoryCountProgressReadModelSchema, value)) return false;
  if (!isInventoryCountCapture(value.capture)) return false;
  if (
    (value.periodMovementCount <= 500 &&
      value.periodMovementCount !== value.periodMovements.length) ||
    (value.periodMovementCount === 501 &&
      (value.periodMovements.length !== 500 || value.canExecute || value.blocker === null))
  ) {
    return false;
  }
  let previousSequence = value.capture.inventoryMovementSequence;
  let movementQuantity = value.capture.theoreticalQuantity;
  for (const movement of value.periodMovements) {
    const expectedAfter = movementQuantity + movement.quantityDelta;
    if (
      movement.sequence <= previousSequence ||
      movement.quantityBefore !== movementQuantity ||
      !Number.isSafeInteger(expectedAfter) ||
      movement.quantityAfter !== expectedAfter
    ) {
      return false;
    }
    previousSequence = movement.sequence;
    movementQuantity = movement.quantityAfter;
  }
  if (
    value.canExecute &&
    value.periodMovements.some(
      (movement) =>
        movement.movementType === "COUNT_SET" || movement.movementType === "MANUAL_ADJUSTMENT",
    )
  ) {
    return false;
  }
  const periodMovementDelta = value.periodMovements.reduce(
    (sum, movement) => sum + movement.quantityDelta,
    0,
  );
  if (!Number.isSafeInteger(periodMovementDelta)) return false;
  const expectedCurrent = value.capture.theoreticalQuantity + periodMovementDelta;
  const expectedProjected = value.capture.countedQuantity + periodMovementDelta;
  if (
    periodMovementDelta !== value.periodMovementDelta ||
    expectedCurrent !== value.currentTheoreticalQuantity ||
    expectedProjected !== value.projectedQuantityAfterApply
  ) {
    return false;
  }
  const minimumInventoryVersion = value.capture.inventoryVersion + value.periodMovementCount;
  if (
    !Number.isSafeInteger(minimumInventoryVersion) ||
    (value.periodMovementCount <= 500
      ? value.currentInventoryVersion !== minimumInventoryVersion
      : value.currentInventoryVersion < minimumInventoryVersion)
  ) {
    return false;
  }
  if (value.currentInventoryValuationVersion === null) {
    if (value.canExecute || value.currentInventoryValueFen !== null) return false;
  } else {
    const capturedValuationVersion = value.capture.inventoryValuationVersion;
    const minimumValuationVersion =
      capturedValuationVersion === null
        ? null
        : capturedValuationVersion + value.periodMovementCount;
    if (
      minimumValuationVersion === null ||
      !Number.isSafeInteger(minimumValuationVersion) ||
      (value.periodMovementCount <= 500
        ? value.currentInventoryValuationVersion !== minimumValuationVersion
        : value.currentInventoryValuationVersion < minimumValuationVersion)
    ) {
      return false;
    }
  }
  if (value.canExecute !== (value.blocker === null)) return false;
  if (value.projectedQuantityAfterApply < value.activeReservedQuantity) return !value.canExecute;
  if (
    value.currentInventoryValueFen === null ||
    value.projectedInventoryValueFen === null ||
    value.inventoryValueDeltaFen === null
  ) {
    return (
      value.currentInventoryValueFen === null &&
      value.projectedInventoryValueFen === null &&
      value.inventoryValueDeltaFen === null &&
      !value.canExecute
    );
  }
  const expectedValueDeltaFen = expectedInventoryCountValueDeltaFen(
    value.capture,
    value.currentTheoreticalQuantity,
    value.currentInventoryValueFen,
  );
  return (
    expectedValueDeltaFen !== null &&
    value.inventoryValueDeltaFen === expectedValueDeltaFen &&
    Number.isSafeInteger(value.currentInventoryValueFen + value.inventoryValueDeltaFen) &&
    value.currentInventoryValueFen + value.inventoryValueDeltaFen ===
      value.projectedInventoryValueFen
  );
}

export function isInventoryCountAppliedFact(value: unknown): value is InventoryCountAppliedFact {
  if (!Value.Check(InventoryCountAppliedFactSchema, value)) return false;
  if (!isInventoryCountCapture(value.capture)) return false;
  const expectedInventoryVersionBefore = value.capture.inventoryVersion + value.periodMovementCount;
  const expectedValuationVersionBefore =
    value.capture.inventoryValuationVersion === null
      ? null
      : value.capture.inventoryValuationVersion + value.periodMovementCount;
  const expectedValueDeltaFen = expectedInventoryCountValueDeltaFen(
    value.capture,
    value.quantityBefore,
    value.inventoryValueBeforeFen,
  );
  return (
    value.capture.inventoryValueFen !== null &&
    value.actorId !== value.authorizerId &&
    expectedValueDeltaFen !== null &&
    value.inventoryValueDeltaFen === expectedValueDeltaFen &&
    expectedValuationVersionBefore !== null &&
    Number.isSafeInteger(expectedInventoryVersionBefore) &&
    Number.isSafeInteger(expectedValuationVersionBefore) &&
    value.executionInventoryMovementSequence > value.capture.inventoryMovementSequence &&
    value.inventoryVersionBefore === expectedInventoryVersionBefore &&
    value.inventoryValuationVersionBefore === expectedValuationVersionBefore &&
    value.quantityDelta === value.capture.differenceQuantity &&
    Number.isSafeInteger(value.capture.theoreticalQuantity + value.periodMovementDelta) &&
    value.quantityBefore === value.capture.theoreticalQuantity + value.periodMovementDelta &&
    value.quantityAfter === value.quantityBefore + value.quantityDelta &&
    value.inventoryVersionAfter === value.inventoryVersionBefore + 1 &&
    value.inventoryValuationVersionAfter === value.inventoryValuationVersionBefore + 1 &&
    Number.isSafeInteger(value.inventoryValueBeforeFen + value.inventoryValueDeltaFen) &&
    value.inventoryValueAfterFen === value.inventoryValueBeforeFen + value.inventoryValueDeltaFen
  );
}
